/*
 * Jira Ticket:
 * Created Date: Mon, 23rd Nov 2020, 19:42:57 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import { ValidationContext } from "graphql";

export const nonNegativeNonZeroInt = (num = 0) => {
  if (num !== null) {
    if (Number.isInteger(num)) {
      if (num <= 0) {
        return 'validation.positivieNonZeroInt';
      }
    } else {
      return 'validation.notAnInt';
    }
  } else {
    return 'ra.validation.required';
  }
};

export const nonNegativeNonZeroNumber = (num = 0) => {
  if (num !== null) {
    if (num <= 0) {
      return 'validation.positivieNonZeroInt';
    }
  } else {
    return 'ra.validation.required';
  }
};

export const nonNegativeInt = (num) => {
  if (num || num === 0) {
    if (Number.isInteger(num)) {
      if (num < 0) {
        return 'validation.positivieInt';
      }
    } else {
      return 'validation.notAnInt';
    }
  } else {
    return 'ra.validation.required';
  }
};

export const isInt = (num) => {
  if (num || num === 0) {
    if (!Number.isInteger(num)) return 'validation.notAnInt';
  } else {
    return 'ra.validation.required';
  }
};

export const nonNegativeNotRequiredInt = (num) => {
  if (num || num === 0) {
    if (Number.isInteger(num)) {
      if (num < 0) {
        return 'validation.positivieInt';
      }
    } else {
      return 'validation.notAnInt';
    }
  }
};

// regex
const emailEx = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/, 'i');

const resourceRouteParent = (resourceName) => {
  return new RegExp('^/' + resourceName + '$', 'i');
};
const resourceRouteChild = (resourceName) => {
  return new RegExp('^/' + resourceName + '/.*', 'i');
};

// regex tests
const isValidEmailTest = (email) => {
  return emailEx.test(email);
};

const isTheCurrentRouteTest = (resource, route) => {
  const resourceRouteParentEx = resourceRouteParent(resource);
  const resourceRouteChildEx = resourceRouteChild(resource);
  return resourceRouteParentEx.test(route) || resourceRouteChildEx.test(route);
};

// validators
const isValidEmail = (email) => {
  if (email && !isValidEmailTest(email)) {
    return 'error.field.text.invalidEmail';
  }
};

const isResourceCurrentRoute = (resource, route) => {
  return resource && route && isTheCurrentRouteTest(resource, route);
};

const integerValidation = (value) => {
  if (!Number.isInteger(value)) return 'error.field.number.notInt';
};

const checkPassword = (values, translate, errors = {}) => {
  // check current password has been entered
  if (!values.currentPassword)
    errors.currentPassword = translate('ra.validation.required');
  // check new password meets criteria
  if (values.newPassword) {
    const { newPassword } = values;
    // Contains special char (?!#%$)?
    if (!new RegExp(/[?!#%$]/i).test(newPassword))
      errors.newPassword = translate('validation.passwdSpecial');
    // Contains number?
    if (!new RegExp(/\d/).test(newPassword))
      errors.newPassword = translate('validation.passwdNumber');
    // Contains lower case letter?
    if (!new RegExp(/[a-z]/).test(newPassword))
      errors.newPassword = translate('validation.passwdLower');
    // Is at least 8 chars long?
    if (newPassword.length < 8)
      errors.newPassword = translate('validation.passwdMinLen');
    // Is at most 99 chars long?
    if (newPassword.length > 99)
      errors.newPassword = translate('validation.passwdMaxLen');
  } else {
    errors.newPassword = translate('ra.validation.required');
  }
  // check new and re-typed passwords match
  if (values.passwordVerify) {
    if (values.newPassword) {
      const { newPassword, passwordVerify } = values;
      // passwords match?
      if (newPassword !== passwordVerify)
        errors.passwordVerify = translate('validation.passwdNoMatch');
    }
  } else {
    errors.passwordVerify = translate('ra.validation.required');
  }

  return errors;
};

const validatePassword = (values, props, errors) => {
  const { translate } = props;
  return checkPassword(values, translate, errors);
};

const validateFourStringCode = (str) => {
  // Fix: 12384
  const match = str.match(/(?!.*(.).*\1)[1-9]{4}/gim);
  if (str.length !== null) {
    if(str.length != 4) {
      return 'validation.validateFourStringCode';
    }
    if (!match) {
      return 'validation.validateFourStringCode';
    }
  } else {
    return 'ra.validation.required';
  }
};

const validateFourStringCodeOptional = (str) => {
  if (!str) {
    return;
  }

  const match = str.match(/(?!.*(.).*\1)[1-9]{4}/gim);
  if (str && str.length !== null) {
    if(str.length != 4) {
      return 'validation.validateFourStringCode';
    }
    if (!match) {
      return 'validation.validateFourStringCode';
    }
  }
}

const validateFolderNumber = (num) => {
  if (num || num === 0) {
    if (Number.isInteger(num)) {
      if (num <= 0 || num > 10) {
        return 'validation.validateFolderNumber';
      }
    } else {
      return 'validation.notAnInt';
    }
  } else {
    return 'ra.validation.required';
  }
}
const validatePostCode = (str) => {
  const match =
    /^(GIR[ ]?0AA|((AB|AL|B|BA|BB|BD|BH|BL|BN|BR|BS|BT|CA|CB|CF|CH|CM|CO|CR|CT|CV|CW|DA|DD|DE|DG|DH|DL|DN|DT|DY|E|EC|EH|EN|EX|FK|FY|G|GL|GY|GU|HA|HD|HG|HP|HR|HS|HU|HX|IG|IM|IP|IV|JE|KA|KT|KW|KY|L|LA|LD|LE|LL|LN|LS|LU|M|ME|MK|ML|N|NE|NG|NN|NP|NR|NW|OL|OX|PA|PE|PH|PL|PO|PR|RG|RH|RM|S|SA|SE|SG|SK|SL|SM|SN|SO|SP|SR|SS|ST|SW|SY|TA|TD|TF|TN|TQ|TR|TS|TW|UB|W|WA|WC|WD|WF|WN|WR|WS|WV|YO|ZE)(\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}))|BFPO[ ]?\d{1,4})$/.test(
      str
    );
  console.log(match);
  if (str.length !== null) {
    if (!match) {
      return 'validation.validatePostCode';
    }
  } else {
    return 'ra.validation.required';
  }
};

const validateDressCode = (str) => {
  if(!str) {
    return;
  }
  if(str&& str.trim().length > 35){
    return 'validation.validateDressCode';
  }
}
export {
  integerValidation,
  isValidEmail,
  isResourceCurrentRoute,
  validatePassword,
  validateFourStringCode,
  validatePostCode,
  validateFolderNumber,
  validateDressCode,
  validateFourStringCodeOptional
};
